/**
 * Shared wizard-step model
 *
 * Single source of the step-descriptor shape used by every multi-step form
 * wizard (task wizard, obstruction wizard, …). A wizard builds ONE ordered
 * list of descriptors; from that list it derives the stepper, the step
 * renderer, per-position validation and the Summary edit-jump mapping. The
 * `currentStep` form field stays purely positional (1-indexed into the list).
 *
 * Validation logic (`validate` / `requiredFields`) is intentionally free of
 * any component import so it can be reused by navigation/submission hooks
 * (and their tests) without pulling step components.
 */
import type { ReactNode } from 'react';

import type { TabIconComponent } from '@/components/common/CollapsibleTabs';

/** Outcome of validating a single wizard step. */
export interface StepValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/** Inputs a step validator may read. Each validator uses what it needs. */
export interface StepValidationContext<TValues> {
  values: TValues;
  formErrors: Partial<Record<keyof TValues, string>>;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * One wizard step. `TValues` is the form value shape, `TRenderProps` the prop
 * bag passed to `render`, `TId` the step's stable identifier (used for the
 * Summary edit-jump mapping, independent of position).
 */
export interface WizardStepDescriptor<
  TValues,
  TRenderProps,
  TId extends string = string,
> {
  id: TId;
  /** Icon component rendered in the section tab / sidebar nav item. */
  icon: TabIconComponent;
  /** i18n key for the section label. */
  labelKey: string;
  /** i18n key for the section description. */
  descKey: string;
  /** Fields that must be filled to leave this step (quick navigation gate). */
  requiredFields: (keyof TValues)[];
  /** Full per-step validation (required + cross-field + enum guards). */
  validate: (context: StepValidationContext<TValues>) => StepValidationResult;
  /** Renders the step body from the shared prop bag. */
  render: (props: TRenderProps) => ReactNode;
  /**
   * The step manages its own internal scroll (pinned header + scrollable list).
   * When set, the renderer skips the shared outer `AppScrollArea` and gives the
   * step a bounded flex-column so its internal scroll regions can size.
   */
  ownsScroll?: boolean;
}

/** Valid result for steps with nothing to validate. */
export const NOOP_STEP_VALIDATION: StepValidationResult = {
  isValid: true,
  errors: {},
};

/**
 * Quick "are the required fields filled / error-free" check, keyed by an
 * explicit field list (NOT a step number) so it works regardless of which
 * steps are present in the current list.
 */
export function validateRequiredFields<TValues extends object>(
  requiredFields: (keyof TValues)[],
  values: TValues,
  formErrors: Partial<Record<keyof TValues, string>>
): StepValidationResult {
  const errors: Record<string, string> = {};

  requiredFields.forEach((field) => {
    const value = values[field];
    const hasError = formErrors[field];
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '');

    if (hasError || isEmpty) {
      errors[String(field)] = hasError ? String(hasError) : 'Field required';
    }
  });

  return { isValid: Object.keys(errors).length === 0, errors };
}
